import { useCallback, useRef, useState, type DragEvent } from 'react';

/** True when the drag carries files from the OS, as opposed to dragged text,
 *  links or an in-page element. Only `types` is readable during dragenter /
 *  dragover; `files` stays empty until the drop. */
export function dragHasFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) return false;
  return Array.from(dataTransfer.types).includes('Files');
}

export interface FileDropHandlers {
  readonly onDragEnter: (e: DragEvent<HTMLElement>) => void;
  readonly onDragOver: (e: DragEvent<HTMLElement>) => void;
  readonly onDragLeave: (e: DragEvent<HTMLElement>) => void;
  readonly onDrop: (e: DragEvent<HTMLElement>) => void;
}

export interface UseFileDropApi {
  /** Files are being dragged over the target and a drop would be accepted. */
  readonly isDragActive: boolean;
  /** Spread onto the element that should accept the drop. */
  readonly dropHandlers: FileDropHandlers;
}

/**
 * Makes an element a drop target for files. Passing `undefined` turns it off:
 * the handlers become no-ops and the browser's own handling applies.
 *
 * dragenter and dragleave fire for every child the pointer crosses, so a
 * single boolean would flicker off whenever the pointer moved onto a child.
 * A depth counter tracks how many nested elements the drag is inside instead.
 */
export function useFileDrop(
  onFiles: ((files: File[]) => void) | undefined
): UseFileDropApi {
  const [isDragActive, setIsDragActive] = useState(false);
  const depthRef = useRef(0);

  const onDragEnter = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles || !dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      depthRef.current += 1;
      setIsDragActive(true);
    },
    [onFiles]
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles || !dragHasFiles(e.dataTransfer)) return;
      // Without preventDefault on dragover the browser refuses the drop.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [onFiles]
  );

  const onDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles || !dragHasFiles(e.dataTransfer)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragActive(false);
    },
    [onFiles]
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles || !dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      depthRef.current = 0;
      setIsDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) onFiles(files);
    },
    [onFiles]
  );

  return {
    isDragActive,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
